// AI Worker editor data (Slice G-L3 UI). The client's guided surface for their
// own worker(s): list them, read the editable projection, and save the safe
// edits (question wording, business hours, handoff sensitivity). Wired to the
// tenant-side GET /workers + GET/PATCH /workers/:id/editable; the backend
// re-validates every save against invariant 22, so a client can't strand the bot.

import type { IdentityProfile } from '@vysto/shared/bot-config/identity'

import { apiClient } from '../../api/client.js'

export interface Worker {
  id: string
  name: string
  role: string
  status: string
  number?: string
}

export interface EditableQuestion {
  nodeId: string
  prompt: string
  /** The field this question captures (e.g. 'nombre'). */
  captures: string
  /** The extraction kind ('string' | 'phone_mx' | 'email' | …). */
  kind: string
}

export interface WorkerEditable {
  id: string
  name: string
  role: string
  status: string
  /** `bot_instances.greeting` — the line the agent opens with (W4'-4a). '' when
   *  nothing is written yet. It is a column again, not the orchestrator's
   *  `start` node: the compiler owns that flow now and refuses hand edits. */
  greeting: string
  /** personalidad.identidad — who the agent is (composed; shown as preview). */
  identidad: string
  /** The guided-identity answers the editor edits; null for legacy/template bots. */
  identityProfile: IdentityProfile | null
  /** conocimiento.no_match_message — Responder's deferral ('' = system default). */
  noMatchMessage: string
  /** personalidad.use_formatting — WhatsApp emphasis on/off (default off). */
  useFormatting: boolean
  /** personalidad.use_emojis — emojis on/off (default off). */
  useEmojis: boolean
  /** bot_instances.requires_confirmation — booking mode (default off = autonomous). */
  requiresConfirmation: boolean
  questions: EditableQuestion[]
  handoff: { failedAttempts: number }
  /** horarios.business_hours as a Mon-first (0=Mon) week; gates the off-hours
   *  auto-reply. Closed days carry no from/to. */
  businessHours: { day: number; open: boolean; from?: string; to?: string }[]
  /** horarios.out_of_hours_message — sent outside business hours ('' = none). */
  outOfHoursMessage: string
  /** horarios.proactive_window.timezone — IANA tz the agent schedules in. */
  timezone: string
  skillId: string | null
}

export interface WorkerEditPatch {
  /** bot_instances.greeting — the agent's opening line. The server rejects an
   *  empty string (min 1): "no greeting" is not something this form can say. */
  greeting?: string
  /** personalidad.identidad — set directly (advanced). The owner UI sends
   *  identityProfile instead. */
  identidad?: string
  /** The guided-identity answers; the API composes identidad from these. */
  identityProfile?: IdentityProfile
  /** conocimiento.no_match_message — Responder's deferral; '' clears to default. */
  noMatchMessage?: string
  /** personalidad.use_formatting / use_emojis — WhatsApp style toggles. */
  useFormatting?: boolean
  useEmojis?: boolean
  /** Booking mode: true = a coordinator confirms each cita. */
  requiresConfirmation?: boolean
  handoff?: { failedAttempts?: number }
  /** Per-day business hours (0=Mon..6=Sun) → horarios.business_hours. */
  businessHours?: { day: number; open: boolean; from?: string; to?: string }[]
  /** horarios.out_of_hours_message; '' clears it. */
  outOfHoursMessage?: string
  /** horarios.proactive_window.timezone (IANA). */
  timezone?: string
  /** Re-word existing questions. */
  questions?: { nodeId: string; prompt: string }[]
  /** Remove questions by node id. */
  removeQuestions?: string[]
  /** Add questions; `after` is an existing node id or another add's tempId.
   *
   *  `kind` is a plain string ON PURPOSE. It used to be a union derived from a
   *  local 6-value array that hand-copied the server's `ADDABLE_KINDS`
   *  (packages/api/src/workers/worker-flow-edit.ts) — and it drifted: the server
   *  gained `enum` (C2's F39 fix, so a question capturing a list dato stops
   *  storing the answer verbatim) and this copy never did, so the browser type
   *  would have rejected the very kind the fix added. A second copy of a
   *  vocabulary is a second source of truth, and the web cannot import from
   *  packages/api. The REAL gate is the server's zod enum
   *  (worker-editable-core.ts:132, `kind: z.enum(ADDABLE_KINDS)`): a bad kind is
   *  a 400 from PATCH /workers/:id/editable, not a silent bad save. Losing the
   *  compile-time narrowing here is the deliberate price of having one list. */
  addQuestions?: {
    tempId: string
    prompt: string
    captures: string
    kind: string
    after: string
  }[]
}

/** GET /workers — the tenant's own AI Workers (session-scoped). */
export async function getWorkers(): Promise<Worker[]> {
  const res = await apiClient.get<{ data: Worker[] }>('/workers')
  return res.data.data
}

/** GET /workers/:id/editable — the safe-edit projection (identity, questions, hours, handoff). */
export async function getWorkerEditable(id: string): Promise<WorkerEditable> {
  const res = await apiClient.get<{ data: WorkerEditable }>(
    `/workers/${encodeURIComponent(id)}/editable`,
  )
  return res.data.data
}

/** PATCH /workers/:id/editable — saves the safe edits; the backend re-validates against invariant 22 so a client can't strand the bot. */
export async function saveWorkerEditable(id: string, patch: WorkerEditPatch): Promise<void> {
  await apiClient.patch(`/workers/${encodeURIComponent(id)}/editable`, patch)
}

/** Operator twins: same projection/patch against the /admin routes —
 *  tenant in the URL, because staff hold no membership in client tenants. */
export async function getAdminWorkerEditable(
  tenantId: string,
  botId: string,
): Promise<WorkerEditable> {
  const res = await apiClient.get<{ data: WorkerEditable }>(
    `/admin/tenants/${encodeURIComponent(tenantId)}/workers/${encodeURIComponent(botId)}/editable`,
  )
  return res.data.data
}

/** PATCH /admin/tenants/:t/workers/:b/editable — operator twin of saveWorkerEditable, same validation. */
export async function saveAdminWorkerEditable(
  tenantId: string,
  botId: string,
  patch: WorkerEditPatch,
): Promise<void> {
  await apiClient.patch(
    `/admin/tenants/${encodeURIComponent(tenantId)}/workers/${encodeURIComponent(botId)}/editable`,
    patch,
  )
}
